import {useCallback, useEffect, useState} from 'react';

import {CanvasRef} from 'canvas-core/chat/bnchCanvasMarkdown';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PlanArtifact, usePlanArtifact} from 'llm/chat-library/layouts/analysis-workspace/usePlanArtifact';
import {CanvasArtifact, useCanvasArtifact} from 'llm/chat-library/useCanvasArtifact';
import {DocumentArtifact, useDocumentArtifact} from 'llm/chat-library/useDocumentArtifact';
import {SourceFileArtifact, useSourceFileArtifact} from 'llm/chat-library/useSourceFileArtifact';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';

export interface UseArtifactsOptions {
  /** The application state from the agent */
  latestApplicationState: object | null;
  /** Whether the sidepanel is expanded (desktop view) */
  isExpanded: boolean;
  /** The chat type - determines which artifacts are available */
  chatType: ChatType;
  /** The chat session ID, needed for some artifact types */
  chatSessionId: string;
}

export interface UseArtifactsResult {
  /** The primary artifact (usually document), or null if none */
  primaryArtifact: DocumentArtifact | null;
  /** The canvas artifact when a canvas preview is active, or null if none */
  canvasArtifact: CanvasArtifact | null;
  /** The secondary artifact (source file for LDI), or null if none */
  secondaryArtifact: SourceFileArtifact | null;
  /** The plan artifact (for Analysis chats), or null if none */
  planArtifact: PlanArtifact | null;
  /** Whether the primary artifact is open in phone view */
  isPhoneArtifactOpen: boolean;
  /** Whether the canvas artifact is open in phone view */
  isPhoneCanvasOpen: boolean;
  /** Open the primary artifact (handles phone vs desktop) */
  openArtifact: () => void;
  /** Close the primary artifact in phone view */
  closePhoneArtifact: () => void;
  /** Open a canvas preview in the side panel (handles phone vs desktop) */
  openCanvas: (canvas: CanvasRef) => void;
  /** Close the canvas artifact in phone view */
  closePhoneCanvas: () => void;
}

/**
 * Hook for managing artifacts in a chat.
 *
 * Supports multiple artifact types based on the chat type:
 * - Document artifacts from DataCaptureAgentState (all relevant chat types)
 * - Canvas artifacts from active_canvas in application state
 * - Source file artifacts for LegacyDataImport chats
 *
 * @returns Artifacts management interface
 */
export function useArtifacts({
  latestApplicationState,
  isExpanded,
  chatType,
  chatSessionId,
}: UseArtifactsOptions): UseArtifactsResult {
  const isPhoneWidth = useIsPhoneWidth();
  const [isPhoneArtifactOpen, setIsPhoneArtifactOpen] = useState<boolean>(false);

  // Document artifact - available for all chat types that have it
  const primaryArtifact = useDocumentArtifact({latestApplicationState});

  const {canvasArtifact, isPhoneCanvasOpen, openCanvas, closePhoneCanvas} = useCanvasArtifact({
    chatSessionId,
    latestApplicationState,
  });

  // Plan artifact - only for Analysis chats
  const rawPlanArtifact = usePlanArtifact({latestApplicationState});
  const planArtifact = chatType === ChatType.ANALYSIS ? rawPlanArtifact : null;

  // Source file artifact - only for Legacy Data Import
  const sourceFileArtifact = useSourceFileArtifact({
    latestApplicationState,
    chatSessionId,
  });
  const secondaryArtifact = chatType === ChatType.LEGACY_DATA_IMPORT ? sourceFileArtifact : null;

  // Open the primary artifact when the sidepanel is expanded or phone artifact is opened
  useEffect(() => {
    if (primaryArtifact && (isExpanded || isPhoneArtifactOpen)) {
      primaryArtifact.open();
    }
  }, [primaryArtifact, isExpanded, isPhoneArtifactOpen]);

  // Handle opening the artifact - different behavior for phone vs desktop
  const openArtifact = useCallback(() => {
    if (isPhoneWidth) {
      setIsPhoneArtifactOpen(true);
    } else {
      SidepanelActions.expand();
    }
  }, [isPhoneWidth]);

  const closePhoneArtifact = useCallback(() => {
    setIsPhoneArtifactOpen(false);
  }, []);

  return {
    primaryArtifact,
    canvasArtifact,
    secondaryArtifact,
    planArtifact,
    isPhoneArtifactOpen,
    isPhoneCanvasOpen,
    openArtifact,
    closePhoneArtifact,
    openCanvas,
    closePhoneCanvas,
  };
}
